import { useState } from 'react';

import { customAxios } from 'api/orval-mutator';
import { createInsightComment } from 'api/generated/insight-comments/insight-comments';
import { updateInsightClientUserAccess } from 'api/generated/user-insight-access/user-insight-access';
import type { InsightCreateRequest } from 'api/generated/schemas/insightCreateRequest';
import type { InsightDto } from 'api/generated/schemas/insightDto';
import type { InsightUserRequest } from 'api/generated/schemas/insightUserRequest';

import {
  useInsight,
  mapNotesToApi,
} from 'components/partials/insight-modal/insight-context';
import type { InsightApiNote } from 'components/partials/insight-modal/insight-context';

/**
 * FE-facing input: strips to {@link InsightCreateRequest} before multipart JSON part.
 */
export interface InsightData {
  name: string;
  description?: string;
  metadata?: unknown;
  autoAssignToUsers?: boolean;
  [key: string]: unknown;
}

const buildInsightCreateRequest = (
  insightData: InsightData
): InsightCreateRequest => ({
  name: insightData.name,
  description: insightData.description ?? '',
  metadata: insightData.metadata as InsightCreateRequest['metadata'],
  autoAssignToUsers: insightData.autoAssignToUsers ?? false,
});

/**
 * Preserves JSON part as `application/json` {@link Blob} for Spring {@code @RequestPart}
 * (Orval-generated {@code createClientInsight} stringifies without part content-type).
 */
const createClientInsightMultipart = async (
  userId: string,
  clientId: number,
  insightData: InsightData,
  file: Blob | File
): Promise<InsightDto> => {
  const formData = new FormData();
  const insightRequest = buildInsightCreateRequest(insightData);

  formData.append(
    'insightRequest',
    new Blob([JSON.stringify(insightRequest)], { type: 'application/json' })
  );
  formData.append('file', file);

  return customAxios<InsightDto>(
    {
      url: `/api/${userId}/clients/${clientId}/insights`,
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    },
    undefined
  );
};

export interface CreateInsightWithDataArgs {
  userId: string;
  clientId: string | number;
  insightData: InsightData;
  file: Blob | File;
  assignedUsers?: unknown[];
}

type CreatedInsight = InsightDto;

export interface UseCreateInsightResult {
  createInsightWithData: (
    args: CreateInsightWithDataArgs
  ) => Promise<CreatedInsight>;
  loading: boolean;
  error: string | null;
}

export const useCreateInsight = (): UseCreateInsightResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { state: insightState } = useInsight();
  const notes: InsightApiNote[] = mapNotesToApi(insightState.notes);

  const createInsightWithData = async ({
    userId,
    clientId,
    insightData,
    file,
    assignedUsers = [],
  }: CreateInsightWithDataArgs): Promise<CreatedInsight> => {
    setLoading(true);
    setError(null);

    try {
      const createdInsight = await createClientInsightMultipart(
        userId,
        Number(clientId),
        insightData,
        file
      );

      const insightId = createdInsight.id;

      if (insightId === undefined) {
        throw new Error('Create insight response missing id');
      }

      if (notes && notes.length > 0) {
        for (let i = 0; i < notes.length; i++) {
          const note = notes[i];

          await createInsightComment(userId, insightId, {
            message: note.message,
            positionXRelative: note.positionXRelative,
            positionYRelative: note.positionYRelative,
          });
        }
      }

      if (assignedUsers && assignedUsers.length > 0) {
        await updateInsightClientUserAccess(
          userId,
          insightId,
          assignedUsers.flatMap((user): InsightUserRequest[] => {
            if (
              typeof user === 'object' &&
              user !== null &&
              'userId' in user &&
              'hasAccess' in user
            ) {
              const userIdValue = Number(user.userId);

              if (!Number.isNaN(userIdValue)) {
                return [
                  { userId: userIdValue, hasAccess: Boolean(user.hasAccess) },
                ];
              }
            }

            return [];
          })
        );
      }

      setLoading(false);

      return createdInsight;
    } catch (err) {
      const errMsg = (err as Error)?.message || 'Failed to create insight';

      console.error('Error creating insight:', err);
      setError(errMsg);
      setLoading(false);
      throw err;
    }
  };

  return {
    createInsightWithData,
    loading,
    error,
  };
};
